
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import {
  cycleHdBodygraphCenterInConfig,
  cycleHdBodygraphChannelInConfig,
  cycleHdBodygraphCircuitInConfig,
  cycleHdBodygraphGateInConfig,
  cycleHdBodygraphGateSetInConfig,
  cycleHdBodygraphStreamInConfig,
  getHdBodygraphCenterColorState,
  getHdBodygraphChannelColorState,
  getHdBodygraphCircuitColorState,
  getHdBodygraphGateColorState,
  getHdBodygraphGateSetColorState,
  getHdBodygraphStreamColorState,
  isHdBodygraphCenterFullyCovered,
  isHdBodygraphCircuitFullyCovered,
  isHdBodygraphGateSetFullyCovered,
  isHdBodygraphStreamFullyCovered,
  resetAllHdBodygraphCentersInConfig,
  resetAllHdBodygraphCircuitsInConfig,
  resetAllHdBodygraphStreamsInConfig,
  resetHdBodygraphGateSetInConfig,
} from '../hd-bodygraph-config.utils';
import { getHdBodygraphCircuitGates } from '../hd-bodygraph-circuit-metadata';
import {
  HdBodygraphIconKey,
  getHdBodygraphIconFaName,
  getHdGateIconFilterKeys,
  getHdGatesWithIcon,
} from '../hd-bodygraph-icons';
import {
  HD_BODYGRAPH_AWARENESS_CENTER_ORDER,
  HdBodygraphAwarenessCenter,
  getHdBodygraphStreamGates,
  getHdBodygraphStreamsByAwareness,
} from '../hd-bodygraph-stream-metadata';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import { HD_MANDALA_PATTERN_OPTIONS_DEFAULT, HdMandalaPatternOptions } from '../../mandala/patterns/hd-mandala-patterns-view.models';
import {
  HD_BODYGRAPH_CHANNELS,
  HD_BODYGRAPH_GATES_BY_CENTER,
} from '../hd-bodygraph-metadata';
import { HD_BODYGRAPH_CIRCUITS, HD_BODYGRAPH_CIRCUIT_THEME_ICONS } from '../hd-bodygraph-circuit-metadata';
import {
  HdBodygraphCenter,
  HdBodygraphChannelColorState,
  HdBodygraphCircuitFamily,
  HdBodygraphColorState,
  HdBodygraphConfig,
} from '../hd-bodygraph.models';
import { HdBodygraphColorConfig, getHdBodygraphContrastTextColor } from '../hd-bodygraph-color-config';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdCircuitInfoService } from '../hd-circuit-info.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdMemoryPanelService } from '../../memory/hd-memory-panel.service';
import { HdMysticalPanelService } from '../../mystical/hd-mystical-panel.service';
import { HD_MYSTICAL_CIRCUIT_GATES, HD_MYSTICAL_ICON } from '../../mystical/hd-mystical.metadata';
import { HdDocPanelService } from '../doc-panel/hd-doc-panel.service';
import { HdActivePaletteService } from '../hd-active-palette.service';

interface HdBodygraphGateViewModel {
  gate: number;
  colorState: HdBodygraphColorState;
  /** De twee keynotes van de poort; enkel gevuld in de keynote-weergave. */
  keynote1: string;
  keynote2: string;
}

type HdBodygraphCenterDefinitionState = 'open' | 'undefined' | 'defined';

interface HdBodygraphCenterViewModel {
  center: HdBodygraphCenter;
  centerLabel: string;
  definitionState: HdBodygraphCenterDefinitionState;
  backgroundColor: string | null;
  textColor: string;
  gates: HdBodygraphGateViewModel[];
}

/**
 * Eén pil per poort-eigenschap (druk, liefde, geheugen, …) in de sectie
 * Poort-eigenschappen van de Misc-tab: de poortset van de eigenschap gedraagt
 * zich als één eenheid, net als een center.
 */
interface HdBodygraphGateAttributeViewModel {
  key: HdBodygraphIconKey;
  label: string;
  /** FontAwesome-naam van het eigenschap-icoon (zonder `fa-`). */
  icon: string;
  gates: readonly number[];
  /** Poortnummers als subtitel: `19 · 38 · 39 · …`. */
  gateList: string;
  colorState: HdBodygraphChannelColorState;
}

interface HdBodygraphChannelViewModel {
  key: string;
  colorState: HdBodygraphChannelColorState;
  isDefined: boolean;
  /** Kanaalnaam ("Het kanaal van: …"); leeg zolang de scope niet geladen is. */
  name: string;
  /** Korte beschrijving ("Een design van: …"). */
  shortDescription: string;
}

/** localStorage-sleutels van de keynote-weergaves (Kanalen- en Gates-tab; per browser een gemak). */
const HD_CONFIGURATOR_CHANNEL_KEYNOTES_STORAGE_KEY = 'hd-configurator-channel-keynotes';
const HD_CONFIGURATOR_GATE_KEYNOTES_STORAGE_KEY = 'hd-configurator-gate-keynotes';

function readStoredFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function storeFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Opslag geblokkeerd (privévenster): de toggle werkt dan enkel voor deze sessie.
  }
}

interface HdBodygraphCircuitViewModel {
  key: string;
  name: string;
  themeLabel?: string;
  themeIcon?: string;
  colorState: HdBodygraphChannelColorState;
}

interface HdBodygraphCircuitFamilyViewModel {
  family: HdBodygraphCircuitFamily;
  label: string;
  circuits: HdBodygraphCircuitViewModel[];
}

interface HdBodygraphStreamViewModel {
  key: string;
  name: string;
  /** Familiekleur (= parent-circuit kleur), als visuele accent op de knop. */
  color: string;
  colorState: HdBodygraphChannelColorState;
}

interface HdBodygraphStreamAwarenessGroupViewModel {
  awareness: HdBodygraphAwarenessCenter;
  label: string;
  subtitle: string;
  streams: HdBodygraphStreamViewModel[];
}

interface HdBodygraphCenterToggleViewModel {
  center: HdBodygraphCenter;
  label: string;
  /** Center-kleur (gedefinieerde-kleur) als visuele accent-dot op de knop. */
  color: string;
  colorState: HdBodygraphChannelColorState;
}

const HD_BODYGRAPH_CIRCUIT_FAMILY_DISPLAY_ORDER: readonly HdBodygraphCircuitFamily[] = [
  'individueel',
  'tribaal',
  'collectief',
];

export type HdBodygraphConfiguratorTabId = 'gates' | 'centers' | 'misc' | 'channels' | 'circuits' | 'streams';

/**
 * Reden waarom de "Bevries circuit bij klik"-toggle disabled is. Bepaalt
 * welke i18n-key gebruikt wordt voor de tooltip (zie spec §4.1).
 */
export type HdCircuitFreezeModeDisabledReason =
  | 'no-preset'
  | 'preset-modified'
  | 'wrong-mode';

export interface HdBodygraphConfiguratorLayoutConfig {
  showGates: boolean;
  showCenters: boolean;
  showMisc: boolean;
  showChannels: boolean;
  showCircuits: boolean;
  showStreams: boolean;
}

export const HD_BODYGRAPH_CONFIGURATOR_DEFAULT_LAYOUT_CONFIG: HdBodygraphConfiguratorLayoutConfig = {
  showGates: true,
  showCenters: false,
  showMisc: false,
  showChannels: true,
  showCircuits: true,
  showStreams: true,
};

export const HD_BODYGRAPH_CONFIGURATOR_CHANNELS_ONLY_LAYOUT_CONFIG: HdBodygraphConfiguratorLayoutConfig = {
  showGates: false,
  showCenters: false,
  showMisc: false,
  showChannels: true,
  showCircuits: true,
  showStreams: true,
};

export const HD_BODYGRAPH_CONFIGURATOR_GATES_ONLY_LAYOUT_CONFIG: HdBodygraphConfiguratorLayoutConfig = {
  showGates: true,
  showCenters: false,
  showMisc: false,
  showChannels: false,
  showCircuits: false,
  showStreams: false,
};

/**
 * Rechtersectie in de chart-editor: gates (bestaand) + centers (hele center
 * highlighten/activeren) + misc (mandala-patroon-overlays op het wiel en
 * bodygraph-sierlagen zoals het monopool met de bewustzijnskristallen).
 */
export const HD_BODYGRAPH_CONFIGURATOR_GATES_CENTERS_MISC_LAYOUT_CONFIG: HdBodygraphConfiguratorLayoutConfig = {
  showGates: true,
  showCenters: true,
  showMisc: true,
  showChannels: false,
  showCircuits: false,
  showStreams: false,
};

/** Filterbare poort-eigenschappen, in dezelfde volgorde als de filters op de minis-cheat-sheet. */
const HD_BODYGRAPH_CONFIGURATOR_GATE_ATTRIBUTE_KEYS: readonly HdBodygraphIconKey[] = getHdGateIconFilterKeys();

/** Alle poorten die minstens één eigenschap dragen: de reikwijdte van de reset in de sectie Poort-eigenschappen. */
const HD_BODYGRAPH_CONFIGURATOR_GATE_ATTRIBUTE_GATES: readonly number[] = [
  ...new Set(HD_BODYGRAPH_CONFIGURATOR_GATE_ATTRIBUTE_KEYS.flatMap(key => [...getHdGatesWithIcon(key)])),
];

const HD_BODYGRAPH_CONFIGURATOR_CENTER_DISPLAY_ORDER: readonly HdBodygraphCenter[] = [
  'hoofd',
  'ajna',
  'keel',
  'zelf',
  'hart',
  'milt',
  'sacraal',
  'solarplexus',
  'root',
];

const HD_BODYGRAPH_CONFIGURATOR_GATE_BODYGRAPH_ORDER: Readonly<Record<HdBodygraphCenter, readonly number[]>> = {
  hoofd: [64, 61, 63],
  ajna: [47, 24, 4, 17, 43, 11],
  keel: [62, 23, 56, 31, 8, 33, 35, 12, 45, 16, 20],
  zelf: [7, 1, 13, 15, 2, 46, 25, 10],
  hart: [26, 51, 21, 40],
  milt: [48, 57, 44, 50, 32, 28, 18],
  sacraal: [5, 14, 29, 42, 3, 9, 59, 34, 27],
  solarplexus: [37, 22, 36, 49, 55, 30, 6],
  root: [53, 60, 52, 54, 38, 58, 19, 39, 41],
};

@Component({
  selector: 'hd-bodygraph-configurator',
  imports: [TranslocoModule],
  templateUrl: './hd-bodygraph-configurator.component.html',
  styleUrl: './hd-bodygraph-configurator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdBodygraphConfiguratorComponent {
  readonly configuration = input.required<HdBodygraphConfig>();
  readonly layoutConfig = input<HdBodygraphConfiguratorLayoutConfig>(HD_BODYGRAPH_CONFIGURATOR_DEFAULT_LAYOUT_CONFIG);
  private readonly activePalette = inject(HdActivePaletteService);
  /** Kleurenpalet; zonder binding (miniaturen, posters) volgt dit het actieve palet van de gebruiker. */
  readonly colorConfigInput = input<HdBodygraphColorConfig | undefined>(undefined, { alias: 'colorConfig' });
  readonly colorConfig = computed(() => this.colorConfigInput() ?? this.activePalette.colorConfig());
  /**
   * Wanneer `true` activeert de "Bevries circuit bij klik"-toggle in de
   * circuits-tab. De parent zet dit op `true` zodra de chart hover-service
   * actief is en er een ongewijzigde geladen preset is. Zie
   * `docs/human-design/hd-circuit-freeze-mode.md` §3.2.
   */
  readonly circuitFreezeModeAvailable = input<boolean>(false);
  /**
   * Reden waarom de toggle disabled is — bepaalt welke tooltip we tonen
   * (zie spec §4.1). Wanneer {@link circuitFreezeModeAvailable} true is
   * wordt deze input genegeerd.
   */
  readonly circuitFreezeModeDisabledReason = input<HdCircuitFreezeModeDisabledReason>('no-preset');
  readonly configurationChange = output<HdBodygraphConfig>();
  readonly fillAllRequested = output<void>();
  readonly clearAllRequested = output<void>();

  /**
   * Misc-tab → Mandala-sectie toggle-state (patroon-overlays op het wiel). De
   * parent houdt de bron en vertaalt dit + de geladen chart naar de
   * wiel-overlay-input.
   */
  readonly mandalaPatternOptions = input<HdMandalaPatternOptions>(HD_MANDALA_PATTERN_OPTIONS_DEFAULT);
  readonly mandalaPatternOptionsChange = output<HdMandalaPatternOptions>();

  /**
   * Misc-tab → Bodygraph-sectie: toont het magnetisch monopool en de
   * bewustzijnskristallen als sier-laag op de bodygraph-chart. De parent
   * houdt de bron en bindt hem door aan de chart-component.
   */
  readonly showMonopoleAndCrystals = input(false);
  readonly showMonopoleAndCrystalsChange = output<boolean>();

  protected readonly sortGatesAscending = signal(false);

  /**
   * Kanalen-tab: compacte nummer-tegels (default, twee per regel) of één
   * kanaal per regel met de keynotes ("Het kanaal van …", "Een design van …").
   * Onthouden per browser.
   */
  protected readonly channelKeynoteView = signal(readStoredFlag(HD_CONFIGURATOR_CHANNEL_KEYNOTES_STORAGE_KEY));

  /**
   * Gates-tab: compacte nummer-tegels (default, drie per regel) of één poort
   * per regel met haar twee keynotes. Onthouden per browser.
   */
  protected readonly gateKeynoteView = signal(readStoredFlag(HD_CONFIGURATOR_GATE_KEYNOTES_STORAGE_KEY));

  protected toggleChannelKeynoteView(): void {
    const next = !this.channelKeynoteView();
    this.channelKeynoteView.set(next);
    storeFlag(HD_CONFIGURATOR_CHANNEL_KEYNOTES_STORAGE_KEY, next);
  }

  protected toggleGateKeynoteView(): void {
    const next = !this.gateKeynoteView();
    this.gateKeynoteView.set(next);
    storeFlag(HD_CONFIGURATOR_GATE_KEYNOTES_STORAGE_KEY, next);
  }

  /** "Het kanaal van: " / "Een design van: " als los label vóór de (vet gezette) waarde. */
  protected readonly channelKeynoteLabels = computed(() => ({
    name: this.labelBeforePlaceholder('humanDesign.properties.channelNameLabel', 'name'),
    description: this.labelBeforePlaceholder('humanDesign.properties.channelDescriptionLabel', 'shortDescription'),
  }));

  private labelBeforePlaceholder(key: string, placeholder: string): string {
    const marker = '\u0001';
    const translated = this.transloco.translate<string>(key, { [placeholder]: marker }, this.lang());
    const markerIndex = translated.indexOf(marker);
    return markerIndex === -1 ? translated : translated.slice(0, markerIndex);
  }

  /**
   * Lokale toggle-state voor de circuit-freeze-modus. Default `false`. Wordt
   * automatisch op `false` gezet wanneer {@link circuitFreezeModeAvailable}
   * naar `false` flipt (via een `effect()` in de constructor).
   */
  protected readonly circuitFreezeMode = signal(false);

  private readonly hoverHighlight = inject(HdChartHoverHighlightService, { optional: true });

  /**
   * Snapshot van de bevroren gates uit {@link HdChartHoverHighlightService}.
   * Wanneer de service niet beschikbaar is (configurator wordt gebruikt
   * buiten een chart-context) is dit altijd de lege set.
   */
  protected readonly frozenGatesSet = computed<ReadonlySet<number>>(() => {
    return this.hoverHighlight?.effectiveGates() ?? new Set<number>();
  });

  protected readonly centerViewModels = computed<HdBodygraphCenterViewModel[]>(() => {
    const configuration = this.configuration();
    const renderState = this.renderStateFactory.create(configuration);
    const gateOrderByCenter = this.sortGatesAscending()
      ? HD_BODYGRAPH_GATES_BY_CENTER
      : HD_BODYGRAPH_CONFIGURATOR_GATE_BODYGRAPH_ORDER;

    const colors = this.colorConfig();
    const lang = this.lang();
    const centerLabels = this.gateInfoService.centerLabelsIn(lang);
    const withKeynotes = this.gateKeynoteView();

    return HD_BODYGRAPH_CONFIGURATOR_CENTER_DISPLAY_ORDER.map(center => {
      const gates = gateOrderByCenter[center].map(gate => {
        const translations = withKeynotes ? this.gateInfoService.gateInfoIn(gate, lang).translations : null;
        return {
          gate,
          colorState: getHdBodygraphGateColorState(configuration, gate),
          keynote1: translations?.keynote1 ?? '',
          keynote2: translations?.keynote2 ?? '',
        };
      });

      const definitionState = this.resolveCenterDefinitionState(renderState.centerStates[center].isDefined, gates);

      const backgroundColor = this.resolveCenterBackgroundColor(center, definitionState, colors);

      return {
        center,
        centerLabel: centerLabels[center],
        definitionState,
        backgroundColor,
        textColor: getHdBodygraphContrastTextColor(backgroundColor),
        gates,
      };
    });
  });

  protected readonly channelViewModels = computed<HdBodygraphChannelViewModel[]>(() => {
    const configuration = this.configuration();
    const renderState = this.renderStateFactory.create(configuration);

    const lang = this.lang();
    return HD_BODYGRAPH_CHANNELS.map(channel => {
      const translation = this.gateInfoService.channelTranslationIn(channel.key, lang);
      return {
        key: channel.key,
        colorState: getHdBodygraphChannelColorState(configuration, channel.key),
        isDefined: renderState.channelStates[channel.key].isDefined,
        name: translation?.name ?? '',
        shortDescription: translation?.shortDescription ?? '',
      };
    });
  });

  protected readonly circuitFamilyViewModels = computed<HdBodygraphCircuitFamilyViewModel[]>(() => {
    const configuration = this.configuration();
    const circuitNames = this.gateInfoService.circuitNamesIn(this.lang());
    const circuitFamilyLabels = this.gateInfoService.circuitFamilyLabelsIn(this.lang());
    const circuitThemeLabels = this.gateInfoService.circuitThemeLabelsIn(this.lang());
    const freezeMode = this.circuitFreezeMode();
    const frozenGates = freezeMode ? this.frozenGatesSet() : null;

    const circuitsByFamily = new Map<HdBodygraphCircuitFamily, HdBodygraphCircuitViewModel[]>();

    for (const circuit of HD_BODYGRAPH_CIRCUITS) {
      const colorState: HdBodygraphChannelColorState = frozenGates != null
        ? (isHdBodygraphCircuitFullyCovered(circuit.key, frozenGates) ? 'highlight' : 'inactive')
        : getHdBodygraphCircuitColorState(configuration, circuit.key);
      const viewModel: HdBodygraphCircuitViewModel = {
        key: circuit.key,
        name: circuitNames[circuit.key]?.name ?? circuit.key,
        themeLabel: circuit.theme ? circuitThemeLabels[circuit.theme] : undefined,
        themeIcon: circuit.theme ? HD_BODYGRAPH_CIRCUIT_THEME_ICONS[circuit.theme] : undefined,
        colorState,
      };

      const list = circuitsByFamily.get(circuit.family) ?? [];
      list.push(viewModel);
      circuitsByFamily.set(circuit.family, list);
    }

    return HD_BODYGRAPH_CIRCUIT_FAMILY_DISPLAY_ORDER
      .filter(family => circuitsByFamily.has(family))
      .map(family => ({
        family,
        label: circuitFamilyLabels[family],
        circuits: circuitsByFamily.get(family) ?? [],
      }));
  });

  protected readonly hasAnyActiveCircuit = computed(() => {
    if (this.circuitFreezeMode()) {
      return (this.hoverHighlight?.frozenItems().length ?? 0) > 0;
    }
    return this.mysticalCircuit().colorState !== 'inactive'
      || this.circuitFamilyViewModels().some(family =>
        family.circuits.some(circuit => circuit.colorState !== 'inactive'),
      );
  });

  protected readonly streamAwarenessGroupViewModels = computed<HdBodygraphStreamAwarenessGroupViewModel[]>(() => {
    const configuration = this.configuration();
    const streamNames = this.gateInfoService.streamNamesIn(this.lang());
    const awarenessLabels = this.gateInfoService.awarenessLabelsIn(this.lang());
    const freezeMode = this.circuitFreezeMode();
    const frozenGates = freezeMode ? this.frozenGatesSet() : null;

    const streamsByAwareness = getHdBodygraphStreamsByAwareness();

    return HD_BODYGRAPH_AWARENESS_CENTER_ORDER
      .filter(awareness => streamsByAwareness[awareness].length > 0)
      .map(awareness => ({
        awareness,
        label: awarenessLabels[awareness].name,
        subtitle: awarenessLabels[awareness].subtitle,
        streams: streamsByAwareness[awareness].map(stream => {
          const colorState: HdBodygraphChannelColorState = frozenGates != null
            ? (isHdBodygraphStreamFullyCovered(stream.key, frozenGates) ? 'highlight' : 'inactive')
            : getHdBodygraphStreamColorState(configuration, stream.key);
          return {
            key: stream.key,
            name: streamNames[stream.key]?.name ?? stream.key,
            color: stream.color,
            colorState,
          };
        }),
      }));
  });

  protected readonly hasAnyActiveStream = computed(() => {
    if (this.circuitFreezeMode()) {
      return (this.hoverHighlight?.frozenItems().length ?? 0) > 0;
    }
    return this.streamAwarenessGroupViewModels().some(group =>
      group.streams.some(stream => stream.colorState !== 'inactive'),
    );
  });

  /**
   * Eén rij per center voor de Centers-tab. In freeze-modus leidt de pill-staat
   * af uit de bevroren gates (highlight wanneer het hele center bevroren is),
   * anders uit de effectieve gate-activaties — exact het stream/circuit-patroon.
   */
  protected readonly centerToggleViewModels = computed<HdBodygraphCenterToggleViewModel[]>(() => {
    const configuration = this.configuration();
    const centerLabels = this.gateInfoService.centerLabelsIn(this.lang());
    const colors = this.colorConfig();
    const freezeMode = this.circuitFreezeMode();
    const frozenGates = freezeMode ? this.frozenGatesSet() : null;

    return HD_BODYGRAPH_CONFIGURATOR_CENTER_DISPLAY_ORDER.map(center => {
      const colorState: HdBodygraphChannelColorState = frozenGates != null
        ? (isHdBodygraphCenterFullyCovered(center, frozenGates) ? 'highlight' : 'inactive')
        : getHdBodygraphCenterColorState(configuration, center);
      return {
        center,
        label: centerLabels[center],
        color: colors.centerDefined[center],
        colorState,
      };
    });
  });

  protected readonly hasAnyActiveCenter = computed(() => {
    if (this.circuitFreezeMode()) {
      return (this.hoverHighlight?.frozenItems().length ?? 0) > 0;
    }
    return this.centerToggleViewModels().some(center => center.colorState !== 'inactive');
  });

  /**
   * Eén pil per poort-eigenschap voor de sectie Poort-eigenschappen (Misc-tab).
   * Pil-staat zoals bij centers: in bevries-modus highlight wanneer alle poorten
   * van de eigenschap bevroren zijn, anders de geaggregeerde staat van de set.
   */
  protected readonly gateAttributeViewModels = computed<HdBodygraphGateAttributeViewModel[]>(() => {
    const configuration = this.configuration();
    const labels = this.gateInfoService.iconLabelsIn(this.lang());
    const freezeMode = this.circuitFreezeMode();
    const frozenGates = freezeMode ? this.frozenGatesSet() : null;

    return HD_BODYGRAPH_CONFIGURATOR_GATE_ATTRIBUTE_KEYS.map(key => {
      const gates = getHdGatesWithIcon(key);
      const colorState: HdBodygraphChannelColorState = frozenGates != null
        ? (isHdBodygraphGateSetFullyCovered(gates, frozenGates) ? 'highlight' : 'inactive')
        : getHdBodygraphGateSetColorState(configuration, gates);
      return {
        key,
        label: labels[key] || key,
        icon: getHdBodygraphIconFaName(key),
        gates,
        gateList: gates.join(' · '),
        colorState,
      };
    });
  });

  protected readonly hasAnyActiveGateAttribute = computed(() => {
    if (this.circuitFreezeMode()) {
      return (this.hoverHighlight?.frozenItems().length ?? 0) > 0;
    }
    return this.gateAttributeViewModels().some(attribute => attribute.colorState !== 'inactive');
  });

  /**
   * Toggle-knop voor de circuit-freeze-modus moet als enabled tonen wanneer
   * de parent meldt dat de modus beschikbaar is. Anders disabled met een
   * context-specifieke tooltip (zie §4.1).
   */
  protected readonly circuitFreezeModeDisabled = computed(() => !this.circuitFreezeModeAvailable());

  /**
   * I18n-key voor de disabled-tooltip op de "Bevries circuit bij klik"-toggle.
   */
  protected readonly circuitFreezeModeDisabledReasonKey = computed(() => {
    switch (this.circuitFreezeModeDisabledReason()) {
      case 'preset-modified':
        return 'humanDesign.chart.circuitConfigurator.freezeMode.disabledReason.presetModified';
      case 'wrong-mode':
        return 'humanDesign.chart.circuitConfigurator.freezeMode.disabledReason.wrongMode';
      default:
        return 'humanDesign.chart.circuitConfigurator.freezeMode.disabledReason.noPreset';
    }
  });

  protected readonly visibleTabs = computed<HdBodygraphConfiguratorTabId[]>(() => {
    const layout = this.layoutConfig();
    const tabs: HdBodygraphConfiguratorTabId[] = [];

    if (layout.showGates) {
      tabs.push('gates');
    }

    if (layout.showCenters) {
      tabs.push('centers');
    }

    if (layout.showMisc) {
      tabs.push('misc');
    }

    if (layout.showChannels) {
      tabs.push('channels');
    }

    if (layout.showCircuits) {
      tabs.push('circuits');
    }

    if (layout.showStreams) {
      tabs.push('streams');
    }

    return tabs;
  });

  protected readonly showTabHeaders = computed(() => this.visibleTabs().length > 1);
  protected readonly activeTab = signal<HdBodygraphConfiguratorTabId>('channels');

  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Effectieve content-taal (settings-override → app-taal). Drijft alle labels. */
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  /** Vertaalhelper die de settings-taal volgt (niet de `| transloco`-app-taal). */
  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params, this.lang());
  }

  constructor() {
    effect(() => {
      const tabs = this.visibleTabs();

      if (tabs.length === 0) {
        return;
      }

      if (!tabs.includes(this.activeTab())) {
        this.activeTab.set(tabs[0]);
      }
    });

    // Wanneer de parent meldt dat freeze-modus niet langer beschikbaar is
    // (modus-wissel naar Edit/spel, of preset gewijzigd), zet de toggle
    // terug op `false`. `_frozenItems` volgt zijn eigen lifecycle.
    effect(() => {
      if (!this.circuitFreezeModeAvailable() && this.circuitFreezeMode()) {
        this.circuitFreezeMode.set(false);
      }
    });
  }

  protected onCircuitFreezeModeChange(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    if (!this.circuitFreezeModeAvailable()) {
      // Defensief: switch hoort disabled te zijn — synchroniseer voor de
      // zekerheid en negeer de change.
      this.circuitFreezeMode.set(false);
      return;
    }
    this.circuitFreezeMode.set(event.target.checked);
  }

  protected selectTab(tabId: HdBodygraphConfiguratorTabId): void {
    this.activeTab.set(tabId);
  }

  /** Toggle van één Mandala-patroon-optie; emit de volledige nieuwe state. */
  protected onMandalaPatternOptionChange(key: keyof HdMandalaPatternOptions, event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.mandalaPatternOptionsChange.emit({
      ...this.mandalaPatternOptions(),
      [key]: event.target.checked,
    });
  }

  /** Toggle van de monopool & bewustzijnskristallen-laag (Bodygraph-sectie). */
  protected onMonopoleAndCrystalsChange(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.showMonopoleAndCrystalsChange.emit(event.target.checked);
  }

  protected cycleGate(gate: number): void {
    this.configurationChange.emit(cycleHdBodygraphGateInConfig(this.configuration(), gate));
  }

  protected cycleChannel(channelKey: string): void {
    this.configurationChange.emit(cycleHdBodygraphChannelInConfig(this.configuration(), channelKey));
  }

  protected onChannelKeydown(event: KeyboardEvent, channelKey: string): void {
    event.preventDefault();
    this.cycleChannel(channelKey);
  }

  protected cycleCircuit(circuitKey: string): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.toggleCircuitInFreeze(circuitKey);
      return;
    }
    this.configurationChange.emit(cycleHdBodygraphCircuitInConfig(this.configuration(), circuitKey));
  }

  protected onCircuitKeydown(event: KeyboardEvent, circuitKey: string): void {
    event.preventDefault();
    this.cycleCircuit(circuitKey);
  }

  /**
   * Pill-klik in freeze-modus: voeg het hele circuit toe wanneer het nog
   * niet fully-covered is, of verwijder alle gates van het circuit per stuk
   * (cascading) wanneer het wel fully-covered is. Bewuste cascading-semantiek
   * — gedeelde gates van overlappende circuits verdwijnen samen, identiek aan
   * het edit-modus-mentale model. Zie
   * `docs/human-design/hd-circuit-freeze-mode.md` §4.3.
   */
  private toggleCircuitInFreeze(circuitKey: string): void {
    const service = this.hoverHighlight;
    if (service == null) return;
    const circuitGates = getHdBodygraphCircuitGates(circuitKey);
    if (circuitGates.length === 0) return;
    const fullyCovered = isHdBodygraphCircuitFullyCovered(circuitKey, this.frozenGatesSet());
    if (fullyCovered) {
      for (const gate of circuitGates) {
        service.removeFromFreeze({ kind: 'gate', gate });
      }
      return;
    }
    service.addGatesToFreeze(circuitGates);
  }

  protected resetAllCircuits(): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.hoverHighlight.unfreezeSnapshot();
      return;
    }
    this.configurationChange.emit(resetAllHdBodygraphCircuitsInConfig(this.configuration()));
  }

  // ---------------------------------------------------------------------------
  // Circuit-info docked panels
  // ---------------------------------------------------------------------------
  // De info-iconen op de circuit-blokken en de algemene info-knop onder de
  // reset-knop openen een FloatingPanel met de circuit-info componenten. We
  // dwingen `openInitiallyDocked: true` af zodat ze altijd in de zijbalk
  // landen — onafhankelijk van de chart-default voor poort-info-panelen. Zie
  // user-request: "info-iconen hoeven de docking setting van de chart niet te
  // respecteren, ze mogen altijd naar fully docked gaan by default".

  private readonly docPanel = inject(HdDocPanelService);
  private readonly circuitInfoService = inject(HdCircuitInfoService);
  private readonly memoryPanel = inject(HdMemoryPanelService);

  /** Vertaalde UI-labels voor info-knop tooltips/labels (volgt actieve taal). */
  protected readonly circuitInfoUiLabels = computed(() => this.circuitInfoService.uiLabelsIn(this.lang()));

  /**
   * Label + subtitel van het geheugencircuit-blok. Het geheugencircuit is geen
   * hoofd- of subcircuit uit {@link HD_BODYGRAPH_CIRCUITS} maar een volledig
   * apart circuit met eigen betekenis; het krijgt daarom een eigen sectie
   * onderaan de circuits-tab. Tekst uit de `human-design/memory`-scope.
   */
  protected readonly memoryCircuit = computed(() => ({
    label: this.memoryText('circuitLabel', 'Geheugencircuit'),
    subtitle: this.memoryText('circuitSubtitle', ''),
  }));

  private memoryText(key: string, fallback: string): string {
    const lang = this.lang();
    return (
      this.gateInfoService.cheatSheetTextIn('memory', key, lang) ||
      this.gateInfoService.cheatSheetTextIn('memory', key, 'nl') ||
      fallback
    );
  }

  private readonly mysticalPanel = inject(HdMysticalPanelService);

  protected readonly mysticalIcon = HD_MYSTICAL_ICON;

  /**
   * Het mystieke circuit (Rave Cosmology II): geen hoofd- of subcircuit uit
   * {@link HD_BODYGRAPH_CIRCUITS} maar een aparte stroom over acht poorten, en
   * daarom een eigen sectie onderaan de circuits-tab, zoals het geheugen. Anders
   * dan het geheugen is het wel een volwaardige circuit-pil: klik cyclet of
   * bevriest de acht poorten, het info-icoon opent het mystiek-paneel. Tekst uit
   * de `human-design/mystical`-scope.
   */
  protected readonly mysticalCircuit = computed(() => {
    const lang = this.lang();
    const text = (key: string, fallback: string) =>
      this.gateInfoService.cheatSheetTextIn('mystical', key, lang)
      || this.gateInfoService.cheatSheetTextIn('mystical', key, 'nl')
      || fallback;
    const colorState: HdBodygraphChannelColorState = this.circuitFreezeMode()
      ? (isHdBodygraphGateSetFullyCovered(HD_MYSTICAL_CIRCUIT_GATES, this.frozenGatesSet()) ? 'highlight' : 'inactive')
      : getHdBodygraphGateSetColorState(this.configuration(), HD_MYSTICAL_CIRCUIT_GATES);
    return {
      label: text('circuitLabel', 'Mystiek circuit'),
      subtitle: text('circuitSubtitle', ''),
      colorState,
    };
  });

  protected cycleMysticalCircuit(): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.toggleGateSetInFreeze(HD_MYSTICAL_CIRCUIT_GATES);
      return;
    }
    this.configurationChange.emit(cycleHdBodygraphGateSetInConfig(this.configuration(), HD_MYSTICAL_CIRCUIT_GATES));
  }

  protected onMysticalCircuitKeydown(event: KeyboardEvent): void {
    event.preventDefault();
    this.cycleMysticalCircuit();
  }

  /** Opent het mystiek-paneel (uitleg, de stroom en het mystieke aspect per poort). */
  protected openMysticalInfo(event: Event): void {
    event.stopPropagation();
    const origin =
      event instanceof MouseEvent ? { x: event.clientX, y: event.clientY } : undefined;
    this.mysticalPanel.openForCircuit(origin);
  }

  /** Opent het memory-circuit-paneel (blokjes per poort, zoals voices/angsten). */
  protected openMemoryInfo(event: Event): void {
    event.stopPropagation();
    const origin =
      event instanceof MouseEvent ? { x: event.clientX, y: event.clientY } : undefined;
    this.memoryPanel.openForCircuit(origin);
  }

  /**
   * Opent het circuit-info-paneel voor het gegeven circuit. Stopt event-
   * propagation zodat de klik op het info-icoon de circuit-toggle (cycleCircuit
   * op het omsluitende blok) NIET triggert.
   */
  protected openCircuitInfo(circuitKey: string, event: Event): void {
    event.stopPropagation();
    this.docPanel.openDocPanel({ kind: 'circuit', circuitKey, variant: 'normal' });
  }

  /**
   * Opent het algemene circuits-overzicht-paneel. Spiegelt
   * {@link openCircuitInfo} maar zonder per-circuit key.
   */
  protected openOverviewInfo(): void {
    this.docPanel.openDocPanel({ kind: 'overview' });
  }

  /**
   * Opent het stream-info-paneel (bewustzijnsstroom-markdown) voor de gegeven
   * stream. Spiegelt {@link openCircuitInfo}: stopt event-propagation zodat de
   * klik op het info-icoon de stream-toggle (cycleStream op het omsluitende
   * blok) NIET triggert.
   */
  protected openStreamInfo(streamKey: string, event: Event): void {
    event.stopPropagation();
    this.docPanel.openDocPanel({ kind: 'stream', streamKey });
  }

  /**
   * Opent het generieke kanaal-info-paneel voor het gegeven kanaal. Stopt
   * event-propagation zodat de klik op het info-icoon het kanaal niet togglet.
   */
  protected openChannelInfo(channelKey: string, event: Event): void {
    event.stopPropagation();
    this.docPanel.openDocPanel({ kind: 'channel', channelKey });
  }

  protected onStreamKeydown(event: KeyboardEvent, streamKey: string): void {
    event.preventDefault();
    this.cycleStream(streamKey);
  }

  protected cycleStream(streamKey: string): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.toggleStreamInFreeze(streamKey);
      return;
    }
    this.configurationChange.emit(cycleHdBodygraphStreamInConfig(this.configuration(), streamKey));
  }

  /**
   * Pill-klik in freeze-modus voor een stream: voeg de 4 stream-gates toe wanneer
   * de stream nog niet fully-covered is, of verwijder ze (cascading) wanneer wel.
   * Identieke semantiek als {@link toggleCircuitInFreeze}, maar op stream-niveau.
   */
  private toggleStreamInFreeze(streamKey: string): void {
    const service = this.hoverHighlight;
    if (service == null) return;
    const streamGates = getHdBodygraphStreamGates(streamKey);
    if (streamGates.length === 0) return;
    const fullyCovered = isHdBodygraphStreamFullyCovered(streamKey, this.frozenGatesSet());
    if (fullyCovered) {
      for (const gate of streamGates) {
        service.removeFromFreeze({ kind: 'gate', gate });
      }
      return;
    }
    service.addGatesToFreeze(streamGates);
  }

  protected resetAllStreams(): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.hoverHighlight.unfreezeSnapshot();
      return;
    }
    this.configurationChange.emit(resetAllHdBodygraphStreamsInConfig(this.configuration()));
  }

  protected cycleCenter(center: HdBodygraphCenter): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.toggleCenterInFreeze(center);
      return;
    }
    this.configurationChange.emit(cycleHdBodygraphCenterInConfig(this.configuration(), center));
  }

  protected onCenterKeydown(event: KeyboardEvent, center: HdBodygraphCenter): void {
    event.preventDefault();
    this.cycleCenter(center);
  }

  /**
   * Pill-klik in freeze-modus voor een center: voeg alle center-gates toe wanneer
   * het center nog niet fully-covered is, of verwijder ze per stuk (cascading)
   * wanneer wel. Identieke semantiek als {@link toggleStreamInFreeze}.
   */
  private toggleCenterInFreeze(center: HdBodygraphCenter): void {
    const service = this.hoverHighlight;
    if (service == null) return;
    const centerGates = HD_BODYGRAPH_GATES_BY_CENTER[center];
    if (centerGates.length === 0) return;
    const fullyCovered = isHdBodygraphCenterFullyCovered(center, this.frozenGatesSet());
    if (fullyCovered) {
      for (const gate of centerGates) {
        service.removeFromFreeze({ kind: 'gate', gate });
      }
      return;
    }
    service.addGatesToFreeze([...centerGates]);
  }

  protected resetAllCenters(): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.hoverHighlight.unfreezeSnapshot();
      return;
    }
    this.configurationChange.emit(resetAllHdBodygraphCentersInConfig(this.configuration()));
  }

  protected cycleGateAttribute(key: HdBodygraphIconKey): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.toggleGateAttributeInFreeze(key);
      return;
    }
    this.configurationChange.emit(cycleHdBodygraphGateSetInConfig(this.configuration(), getHdGatesWithIcon(key)));
  }

  protected onGateAttributeKeydown(event: KeyboardEvent, key: HdBodygraphIconKey): void {
    event.preventDefault();
    this.cycleGateAttribute(key);
  }

  /**
   * Pil-klik in bevries-modus voor een poort-eigenschap: voeg alle poorten van
   * de eigenschap toe wanneer ze nog niet volledig bevroren zijn, of verwijder
   * ze per stuk (cascading) wanneer wel. Identieke semantiek als
   * {@link toggleCenterInFreeze}.
   */
  private toggleGateAttributeInFreeze(key: HdBodygraphIconKey): void {
    this.toggleGateSetInFreeze(getHdGatesWithIcon(key));
  }

  /**
   * Bevries-toggle voor een willekeurige poortset (poort-eigenschap, mystiek
   * circuit): toevoegen wanneer nog niet volledig bevroren, anders per poort
   * verwijderen (cascading), zoals bij circuits en centers.
   */
  private toggleGateSetInFreeze(gates: readonly number[]): void {
    const service = this.hoverHighlight;
    if (service == null) return;
    if (gates.length === 0) return;
    if (isHdBodygraphGateSetFullyCovered(gates, this.frozenGatesSet())) {
      for (const gate of gates) {
        service.removeFromFreeze({ kind: 'gate', gate });
      }
      return;
    }
    service.addGatesToFreeze(gates);
  }

  /** Reset van de sectie Poort-eigenschappen: ontdooien, of alle poorten met een eigenschap op inactief. */
  protected resetAllGateAttributes(): void {
    if (this.circuitFreezeMode() && this.hoverHighlight != null) {
      this.hoverHighlight.unfreezeSnapshot();
      return;
    }
    this.configurationChange.emit(
      resetHdBodygraphGateSetInConfig(this.configuration(), HD_BODYGRAPH_CONFIGURATOR_GATE_ATTRIBUTE_GATES),
    );
  }

  protected onGateSortModeChange(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.sortGatesAscending.set(event.target.checked);
  }

  protected getStateLabel(colorState: HdBodygraphColorState | HdBodygraphChannelColorState): string {
    switch (colorState) {
      case 'personality':
        return this.t('humanDesign.chart.configurator.state.personality');
      case 'design':
        return this.t('humanDesign.chart.configurator.state.design');
      case 'both':
        return this.t('humanDesign.chart.configurator.state.both');
      case 'highlight':
        return this.t('humanDesign.chart.configurator.state.highlight');
      case 'mixed':
        return this.t('humanDesign.chart.configurator.state.mixed');
      default:
        return this.t('humanDesign.chart.configurator.state.empty');
    }
  }

  protected getVisibleStateLabel(colorState: HdBodygraphColorState | HdBodygraphChannelColorState): string {
    return '';
  }

  protected getStateClass(colorState: HdBodygraphColorState | HdBodygraphChannelColorState): string {
    return `state-${colorState}`;
  }

  protected getCenterDefinitionLabel(state: HdBodygraphCenterDefinitionState): string {
    switch (state) {
      case 'defined':
        return this.t('humanDesign.chart.configurator.definition.defined');
      case 'undefined':
        return this.t('humanDesign.chart.configurator.definition.undefined');
      case 'open':
        return this.t('humanDesign.chart.configurator.definition.open');
    }
  }

  private resolveCenterDefinitionState(
    isDefined: boolean,
    gates: HdBodygraphGateViewModel[],
  ): HdBodygraphCenterDefinitionState {
    if (isDefined) {
      return 'defined';
    }

    const hasActiveGate = gates.some(gate => gate.colorState !== 'inactive');
    return hasActiveGate ? 'undefined' : 'open';
  }

  private resolveCenterBackgroundColor(
    center: HdBodygraphCenter,
    definitionState: HdBodygraphCenterDefinitionState,
    colors: HdBodygraphColorConfig,
  ): string | null {
    switch (definitionState) {
      case 'defined':
        return colors.centerDefined[center];
      case 'undefined':
        return colors.centerUndefined;
      case 'open':
        return colors.centerOpen;
    }
  }

  protected getGateAriaLabel(gate: number, colorState: HdBodygraphColorState): string {
    return this.t('humanDesign.chart.configurator.aria.gate', {
      gate,
      status: this.getStateLabel(colorState),
    });
  }

  protected getChannelAriaLabel(channelKey: string, colorState: HdBodygraphChannelColorState, isDefined: boolean): string {
    const definition = this.t(
      isDefined
        ? 'humanDesign.chart.configurator.aria.channelDefined'
        : 'humanDesign.chart.configurator.aria.channelUndefined',
    );
    return this.t('humanDesign.chart.configurator.aria.channel', {
      channel: channelKey,
      status: this.getStateLabel(colorState),
      definition,
    });
  }

  protected getCircuitAriaLabel(name: string, colorState: HdBodygraphChannelColorState): string {
    return this.t('humanDesign.chart.configurator.aria.circuit', {
      name,
      status: this.getStateLabel(colorState),
    });
  }

  protected getStreamAriaLabel(name: string, colorState: HdBodygraphChannelColorState): string {
    return this.t('humanDesign.chart.configurator.aria.stream', {
      name,
      status: this.getStateLabel(colorState),
    });
  }

  protected getCenterAriaLabel(name: string, colorState: HdBodygraphChannelColorState): string {
    return this.t('humanDesign.chart.configurator.aria.center', {
      name,
      status: this.getStateLabel(colorState),
    });
  }

  protected getGateAttributeAriaLabel(name: string, colorState: HdBodygraphChannelColorState): string {
    return this.t('humanDesign.chart.configurator.aria.gateAttribute', {
      name,
      status: this.getStateLabel(colorState),
    });
  }

  protected getTabLabel(tabId: HdBodygraphConfiguratorTabId): string {
    return this.t(`humanDesign.chart.configurator.tabs.${tabId}`);
  }
}
